import { Drawer } from 'almanac-ui/Drawer/Drawer'
import { observer } from 'mobx-react-lite'
import Styles from './BlogMetadataPanel.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BlogMetadata as BlogMetadataIcon } from 'blaze-ui/icons/36/BlogMetadata'
import { BlogMetadataContainer } from 'components/BlogMetadata/PollingBlogMetadata'
import { useBlogMetadataRecord } from 'hooks/queries/blogMedatadataRecords/useBlogMetadataRecord'
import { Spinner } from 'common/Spinner/Spinner'
import { Text } from 'blaze-ui/Text/Text'
import { AIJobStatus } from 'types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

type Props = {
  articleId: string
}

const getStatusInfo = (status?: AIJobStatus) => {
  switch (status) {
    case AIJobStatus.Pending:
      return { message: 'Generating blog metadata...', isLoading: true }
    case AIJobStatus.Started:
      return { message: 'Blog metadata generation in progress...', isLoading: true }
    case AIJobStatus.Failed:
      return { message: 'Blog metadata generation failed', isLoading: false }
    case AIJobStatus.Successful:
      return { message: '', isLoading: false }
    default:
      return { message: '', isLoading: false }
  }
}

export const BlogMetadataPanel = observer(({ articleId }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { data: blogMetadata, isLoading, error } = useBlogMetadataRecord()

  const headingLevel = isSystemRedesignEnabled ? 5 : 4

  if (isLoading && !blogMetadata) {
    return (
      <>
        <Drawer.Header>
          <div className={Styles.headerInner}>
            <BlogMetadataIcon size={20} />
            <Heading level={headingLevel} lineClamp={1}>
              Configure Metadata
            </Heading>
          </div>
          <Drawer.CloseButton />
        </Drawer.Header>
        <Drawer.Body>
          <div className={Styles.loadingContainer}>
            <Spinner />
            <div className={Styles.statusMessage}>
              <Text variant="secondary">Loading blog metadata...</Text>
            </div>
          </div>
        </Drawer.Body>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Drawer.Header>
          <div className={Styles.headerInner}>
            <BlogMetadataIcon size={20} />
            <Heading level={headingLevel} lineClamp={1}>
              Configure Metadata
            </Heading>
          </div>
          <Drawer.CloseButton />
        </Drawer.Header>
        <Drawer.Body>
          <div className={Styles.errorContainer}>
            <Text variant="secondary" color="error">
              Failed to load blog metadata
            </Text>
          </div>
        </Drawer.Body>
      </>
    )
  }

  const statusInfo = getStatusInfo(blogMetadata?.status)

  if (statusInfo.isLoading) {
    return (
      <>
        <Drawer.Header>
          <div className={Styles.headerInner}>
            <BlogMetadataIcon size={20} />
            <Heading level={headingLevel} lineClamp={1}>
              Configure Metadata
            </Heading>
          </div>
          <Drawer.CloseButton />
        </Drawer.Header>
        <Drawer.Body>
          <div className={Styles.loadingContainer}>
            <Spinner />
            <div className={Styles.statusMessage}>
              <Text variant="secondary">{statusInfo.message}</Text>
            </div>
            {blogMetadata?.failure_reason && (
              <div className={Styles.failureMessage}>
                <Text variant="secondary" color="error">
                  {blogMetadata.failure_reason}
                </Text>
              </div>
            )}
          </div>
        </Drawer.Body>
      </>
    )
  }

  if (blogMetadata?.status === AIJobStatus.Failed) {
    return (
      <>
        <Drawer.Header>
          <div className={Styles.headerInner}>
            <BlogMetadataIcon size={20} />
            <Heading level={headingLevel} lineClamp={1}>
              Configure Metadata
            </Heading>
          </div>
          <Drawer.CloseButton />
        </Drawer.Header>
        <Drawer.Body>
          <div className={Styles.errorContainer}>
            <div className={Styles.centeredText}>
              <Text variant="secondary" color="error">
                {statusInfo.message}
                {blogMetadata?.failure_reason && ` - ${blogMetadata.failure_reason}`}
              </Text>
            </div>
          </div>
        </Drawer.Body>
      </>
    )
  }

  return (
    <>
      <Drawer.Header>
        <div className={Styles.headerInner}>
          <BlogMetadataIcon size={20} />
          <Heading level={headingLevel} lineClamp={1}>
            Configure Metadata
          </Heading>
        </div>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <BlogMetadataContainer articleId={articleId} />
      </Drawer.Body>
    </>
  )
})
